import type { NotificationView } from '../types'
import type { CopyRenderer, CopySegment } from './types'
import { shareEntityNoun } from './share'

/**
 * Cermin orang tua copy — the parent's view of what their connected child was just told.
 *
 * Two rules hold across every renderer here, and they are the whole point of the feature:
 *
 * 1. **Third person about the child, always.** The child-side sentence says "buat kamu" /
 *    "hasil kerja kamu". A parent reading that would think the work is theirs, which is exactly
 *    the confusion this design exists to avoid.
 * 2. **Name the child.** A parent with three children needs to know which one each card is about
 *    before reading anything else, so the name carries the emphasis and leads wherever the
 *    sentence allows it.
 *
 * Rule 1 governs the *child*, not whoever acted. A parent who recommends a book to their own
 * child receives this card too, and naming them in the third person on a card only they can see
 * reads as if somebody else had sent it — so that one sentence switches to "Kamu ...". The
 * snapshot says which case it is (`actorIsRecipient`); a `NotificationView` carries no reader id
 * of its own, and the guardian mirrors are written one event per (parent, child), so the answer
 * is the same for every reader of the event.
 *
 * `RECIPIENT` only. The sender's own receipt is the `content.shared` `ACTOR` row, which is a
 * different card saying a different thing ("... menerima rekomendasi ... darimu").
 */

/** Only reachable on a hand-seeded row — every producer snapshots the name. */
const UNKNOWN_CHILD = 'Anak Anda'

function childNameOf(view: NotificationView): string {
  return view.snapshot.studentName ?? UNKNOWN_CHILD
}

/**
 * The secondary line, same rule as the child-side assignment copy: a lesson name only reads
 * correctly next to its course, and only while the card still covers a single piece of work.
 */
function contextLineOf(view: NotificationView): string | undefined {
  const { courseName, lessonName } = view.context
  if (lessonName && courseName && view.subjectCount <= 1) return `${lessonName} dalam tugas ${courseName}`
  return courseName ?? lessonName
}

/** Roll-up totals are only trustworthy from 1 up; a 0 from a `subjectDelta: 0` producer reads as 1. */
function subjectsIn(view: NotificationView): number {
  return Math.max(1, view.subjectCount)
}

/** "Ada tugas baru untuk {Anak}." */
export const renderGuardianAssignmentAssignedCopy: CopyRenderer = (view) => ({
  body: [
    { text: 'Ada ' },
    { text: 'tugas baru', bold: true },
    { text: ' untuk ' },
    { text: childNameOf(view), bold: true },
    { text: '.' },
  ],
  contextLine: contextLineOf(view),
})

/**
 * "Nilai hasil kerja {Anak} sudah keluar."
 * "Nilai 4 hasil kerja {Anak} sudah keluar."
 *
 * Counts the same occurrences the child's own card counts — a teacher marking six lessons of one
 * course in a sitting is one card on both sides, not six.
 */
export const renderGuardianAssignmentGradedCopy: CopyRenderer = (view) => {
  const graded = subjectsIn(view)

  const body: CopySegment[] = [
    { text: 'Nilai ' },
    graded === 1 ? { text: 'hasil kerja', bold: true } : { text: `${graded} hasil kerja`, bold: true },
    { text: ' ' },
    { text: childNameOf(view), bold: true },
    { text: ' sudah keluar.' },
  ]

  return { body, contextLine: contextLineOf(view) }
}

/** "{Anak} punya tugas yang mendekati tenggat." */
export const renderGuardianAssignmentDueSoonCopy: CopyRenderer = (view) => ({
  body: [
    { text: childNameOf(view), bold: true },
    { text: ' punya tugas yang ' },
    { text: 'mendekati tenggat', bold: true },
    { text: '.' },
  ],
  contextLine: contextLineOf(view),
})

/** "Ada tantangan baru untuk {Anak}." */
export const renderGuardianChallengeAssignedCopy: CopyRenderer = (view) => ({
  body: [
    { text: 'Ada ' },
    { text: 'tantangan baru', bold: true },
    { text: ' untuk ' },
    { text: childNameOf(view), bold: true },
    { text: '.' },
  ],
  contextLine: contextLineOf(view),
})

/** "{Anak} punya tantangan yang mendekati tenggat." */
export const renderGuardianChallengeDueSoonCopy: CopyRenderer = (view) => ({
  body: [
    { text: childNameOf(view), bold: true },
    { text: ' punya tantangan yang ' },
    { text: 'mendekati tenggat', bold: true },
    { text: '.' },
  ],
  contextLine: contextLineOf(view),
})

/**
 * "{Pengirim} merekomendasikan buku {Judul} untuk {Anak}."
 * "Kamu merekomendasikan buku {Judul} untuk {Anak}." — when the reader sent it themselves.
 *
 * The only guardian renderer that names a second person: on the others the school is implicit,
 * but a recommendation is somebody's suggestion and a parent will want to know whose. It is also
 * the only one a parent can trigger, hence the second-person branch — "Bu Sari merekomendasikan"
 * is right for the school's send and wrong for the reader's own.
 */
export const renderGuardianContentSharedCopy: CopyRenderer = (view) => {
  const title = view.snapshot.title ?? 'konten ini'
  const opening: CopySegment = view.snapshot.actorIsRecipient
    ? // Not bold: "Kamu" is a pronoun, and the emphasis belongs on what was sent and to whom.
      { text: 'Kamu' }
    : { text: view.snapshot.actorName ?? 'Seseorang', bold: true }

  return {
    body: [
      opening,
      { text: ` merekomendasikan ${shareEntityNoun(view.entity?.type)} ` },
      { text: title, bold: true },
      { text: ' untuk ' },
      { text: childNameOf(view), bold: true },
      { text: '.' },
    ],
    contextLine: contextLineOf(view),
  }
}
